import { publish } from '../../services/utils/pubsub';
import JxFormBase from './JxFormBase';
import { JxWindowEvents } from '../JxWindowManager/JxWindowEvents';
import { clampPos } from './jxFormGeoUtils';
import jxDockEngine from '../JxDock/JxDockEngine';
import { findDockHost } from '../JxDock/jxDockHitTest';
import { dockHintText } from '../JxDock/jxDockHint';

/**
 * JxFormDraggableBase — drag-to-move.
 * Geo itself (initial resolution: cache-or-default) is owned by JxFormBase.componentDidMount —
 * this class only ever reads/writes state.geo.top/left while dragging, never initializes it.
 */
export default class JxFormDraggableBase extends JxFormBase {
    state = {
        ...this.state,
        dragging: false,
    };

    _dragOffset = { x: 0, y: 0 };
    _dragMoved = false;
    _lastPointer = null; // { clientX, clientY } of the last mousemove, for Ctrl key changes
    _dockHint = null; // { x, y, text } cursor-following hint, rendered by JxFormDockableBase

    componentWillUnmount() {
        super.componentWillUnmount();
    }

    getTarget() {
        return this.getId();
    }

    getObjectType() {
        return this.name;
    }

    _onMouseDown = (e) => {
        if (e.button !== 0) return;
        if (e.target.closest('button')) return;
        e.preventDefault();

        this.bringToTop();
        this._dragOffset = { x: e.clientX - this.state.geo.left, y: e.clientY - this.state.geo.top };
        this._dragMoved = false;
        this.setState({ dragging: true });

        document.body.style.userSelect = 'none';
        document.body.classList.add('jx-dragging');
        document.body.style.cursor = 'grabbing';

        window.addEventListener('mousemove', this._onMouseMoveBound, true);
        window.addEventListener('mouseup', this._onMouseUpBound, true);
        window.addEventListener('keydown', this._onDragKey, true);
        window.addEventListener('keyup', this._onDragKey, true);
    };

    // Ctrl pressed/released without moving the mouse — refresh dock hover + hint
    _onDragKey = (e) => {
        if (e.key !== 'Control' || !this._lastPointer) return;
        this._updateDockHover({ ...this._lastPointer, ctrlKey: e.ctrlKey });
        this.forceUpdate();
    };

    // Stable references for addEventListener/removeEventListener — arrow fields delegate to
    // overridable prototype methods below. Arrow class fields can't call super(), so the
    // actual logic lives in handleMouseMove/handleMouseUp (see Development.Patterns rk=7ac11d29).
    _onMouseMoveBound = (ev) => this.handleMouseMove(ev);
    _onMouseUpBound = (ev) => this.handleMouseUp(ev);

    /**
     * Updates geo.top/left in state as the pointer moves during drag.
     * Prototype method (not arrow field) so subclasses can override + call super.handleMouseMove().
     * @param {MouseEvent} ev
     */
    handleMouseMove(ev) {
        ev.stopImmediatePropagation();
        const clamped = clampPos({
            left: ev.clientX - this._dragOffset.x,
            top: ev.clientY - this._dragOffset.y,
        });
        this._dragMoved = true;
        this._lastPointer = { clientX: ev.clientX, clientY: ev.clientY };
        this._updateDockHover(ev);
        this.setState({ geo: { ...this.state.geo, ...clamped } });
    }

    /**
     * Ends the drag — resets cursor/body state, publishes final pos, removes listeners.
     * Prototype method — subclasses overriding this MUST call super.handleMouseUp().
     */
    handleMouseUp(ev) {
        document.body.style.userSelect = '';
        document.body.classList.remove('jx-dragging');
        document.body.style.cursor = '';
        this.setState({ dragging: false });

        this.publishWindowPos();

        window.removeEventListener('mousemove', this._onMouseMoveBound, true);
        window.removeEventListener('mouseup', this._onMouseUpBound, true);
        window.removeEventListener('keydown', this._onDragKey, true);
        window.removeEventListener('keyup', this._onDragKey, true);
        this._lastPointer = null;
        this._dockHint = null;

        this._tryDock(ev);
    }

    // Overridden by JxFormDockableBase — plain windows are never docked
    isDockable() {
        return false;
    }

    // Dock host under the cursor, only while Ctrl is held — plain drag just moves the window
    _findDockTarget(ev) {
        if (!ev?.ctrlKey) return null;
        if (!this.isDockable()) return null;
        if (jxDockEngine.hasTabs(this.getId())) return null;
        return findDockHost(ev.clientX, ev.clientY, this.getId(), jxDockEngine.getPanelIds());
    }

    _updateDockHover(ev) {
        const targetId = this._findDockTarget(ev);
        jxDockEngine.setHover(targetId, targetId ? this.getId() : null);
        this._dockHint = this._buildDockHint(ev, targetId);
    }

    // Only for windows that can dock right now and have another window to dock into
    _buildDockHint(ev, targetId) {
        if (!this.isDockable() || jxDockEngine.hasTabs(this.getId())) return null;
        if (jxDockEngine.getPanelIds().length < 2) return null;

        const text = dockHintText(ev.ctrlKey, targetId ? jxDockEngine.getTitle(targetId) : null);
        return { x: ev.clientX, y: ev.clientY, text };
    }

    _tryDock(ev) {
        jxDockEngine.setHover(null, null);
        if (!this._dragMoved || !ev) return;
        const hostId = this._findDockTarget(ev);
        if (hostId) jxDockEngine.dock(hostId, this.getId());
    }

    publishWindowPos() {
        const { top, left, width, height } = this.state.geo;
        publish(JxWindowEvents.channel, {
            name: JxWindowEvents.onPosChanged,
            target: this.getTarget(),
            data: { posX: left, posY: top, width, height, component: this.getObjectType() },
        });
    }

    get dragHandleProps() {
        return {
            onMouseDown: this._onMouseDown,
            style: { cursor: this.state.dragging ? 'grabbing' : 'grab' },
        };
    }

    get panelStyle() {
        return { top: this.state.geo.top, left: this.state.geo.left, zIndex: this.state.zIndex };
    }

    get panelProps() {
        return {
            id: this.getId(),
            onMouseDown: this.bringToTop,
        };
    }
}
